import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { BarChart3, TrendingDown, TrendingUp, Minus, MapPin, RotateCcw } from "lucide-react";
import { api } from "@/lib/api";

const PALETTE = ["#FACC15", "#09090B", "#F97316", "#2563EB", "#16A34A", "#DC2626", "#7C3AED", "#0891B2", "#A16207"];

function Trend({ value, testId }) {
  if (value == null) return <span className="text-zinc-400" data-testid={testId}>—</span>;
  const Icon = value > 0 ? TrendingUp : value < 0 ? TrendingDown : Minus;
  const cls = value > 0 ? "text-[#B91C1C]" : value < 0 ? "text-[#15803D]" : "text-zinc-500";
  return (
    <span className={`inline-flex items-center gap-1 font-mono text-sm font-bold ${cls}`} data-testid={testId}>
      <Icon className="h-3.5 w-3.5" /> {value > 0 ? "+" : ""}{value}%
    </span>
  );
}

function Stat({ label, value, sub, testId }) {
  return (
    <div className="rounded-xl border-2 border-black bg-white p-4" data-testid={testId}>
      <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500">{label}</p>
      <p className="mt-1 font-display text-3xl font-extrabold">{value}</p>
      {sub && <p className="text-xs text-zinc-500">{sub}</p>}
    </div>
  );
}

function WeeklyBars({ weeks, order }) {
  const max = Math.max(1, ...weeks.map((w) => w.total));
  const color = (p) => PALETTE[order.indexOf(p) % PALETTE.length];
  return (
    <div className="rounded-2xl border-2 border-black bg-white p-4" data-testid="analytics-chart">
      <div className="flex h-56 items-end gap-2 sm:gap-3">
        {weeks.map((w, i) => (
          <div key={w.week_start} className="flex flex-1 flex-col items-center gap-1" data-testid={`week-bar-${w.week_start}`}>
            <span className="font-mono text-xs font-bold">{w.total}</span>
            <div className="flex w-full flex-col-reverse overflow-hidden rounded-md border-2 border-black" style={{ height: `${Math.max(4, (w.total / max) * 180)}px` }} title={`Week of ${w.label}: ${w.total}`}>
              {order.map((p) =>
                w.by_product[p] ? (
                  <div key={p} style={{ height: `${(w.by_product[p] / w.total) * 100}%`, background: color(p) }} title={`${p}: ${w.by_product[p]}`} />
                ) : null
              )}
            </div>
            <span className={`text-[10px] font-semibold ${i === weeks.length - 1 ? "text-black" : "text-zinc-500"}`}>{i === weeks.length - 1 ? "This wk" : w.label}</span>
          </div>
        ))}
      </div>
      <div className="mt-4 flex flex-wrap gap-x-4 gap-y-1 text-xs" data-testid="analytics-legend">
        {order.map((p) => (
          <span key={p} className="inline-flex items-center gap-1.5">
            <span className="inline-block h-3 w-3 rounded-sm border border-black" style={{ background: color(p) }} /> {p}
          </span>
        ))}
      </div>
    </div>
  );
}

export function Analytics() {
  const [weeks, setWeeks] = useState(8);
  const { data } = useQuery({ queryKey: ["analytics", weeks], queryFn: () => api.get("/admin/analytics", { params: { weeks } }).then((r) => r.data) });
  if (!data) return <p className="text-zinc-600" data-testid="analytics-loading">Loading analytics…</p>;
  const wow = data.last_week ? Math.round((100 * (data.this_week - data.last_week)) / data.last_week) : data.this_week ? 100 : null;
  return (
    <div className="space-y-6" data-testid="analytics-panel">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="flex items-center gap-2 font-display text-xl font-bold"><BarChart3 className="h-5 w-5" /> What generates tickets</h2>
          <p className="text-sm text-zinc-600">Tickets by week, product and query so you can fix root causes instead of answering the same question twice.</p>
        </div>
        <div className="inline-flex rounded-xl border-2 border-black bg-white p-1" data-testid="analytics-range">
          {[4, 8, 12].map((n) => (
            <button key={n} type="button" onClick={() => setWeeks(n)} className={`rounded-lg px-3 py-1.5 text-sm font-bold ${weeks === n ? "bg-black text-white" : "hover:bg-[#FEF9C3]"}`} data-testid={`analytics-range-${n}`}>
              {n} wks
            </button>
          ))}
        </div>
      </div>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Stat label="This week" value={data.this_week} sub={<span className="inline-flex items-center gap-1">vs last week {data.last_week} <Trend value={wow} testId="analytics-wow" /></span>} testId="analytics-this-week" />
        <Stat label={`Avg per week (${weeks} wks)`} value={data.avg_per_week} sub={`${data.total} tickets total`} testId="analytics-avg-week" />
        <Stat label="Complaints share" value={data.total ? `${Math.round((100 * data.qrc.Complaint) / data.total)}%` : "—"} sub={`${data.qrc.Complaint} complaints · ${data.qrc.Request} requests · ${data.qrc.Query} queries`} testId="analytics-complaints" />
        <Stat label="Repeat issues" value={data.repeat_issues} sub={<span className="inline-flex items-center gap-1"><RotateCcw className="h-3 w-3" /> reopened after resolution · <MapPin className="h-3 w-3" /> {data.offline_interest} Indore leads</span>} testId="analytics-repeat" />
      </div>

      <WeeklyBars weeks={data.weeks} order={data.product_order} />

      <div className="grid gap-6 lg:grid-cols-2">
        <section className="overflow-x-auto rounded-2xl border-2 border-black bg-white">
          <h3 className="border-b-2 border-black bg-[#FACC15] px-4 py-3 font-display text-base font-bold">By product</h3>
          <table className="w-full text-left text-sm" data-testid="analytics-products">
            <thead className="text-[10px] uppercase tracking-wider text-zinc-500">
              <tr><th className="px-4 py-2">Product</th><th className="px-2 py-2 text-right">Total</th><th className="px-2 py-2 text-right">This wk</th><th className="px-2 py-2 text-right">Last wk</th><th className="px-4 py-2 text-right">Change</th></tr>
            </thead>
            <tbody className="divide-y divide-zinc-200">
              {data.products.map((p) => (
                <tr key={p.product} data-testid={`analytics-product-${p.product}`}>
                  <td className="px-4 py-2 font-semibold">{p.product}</td>
                  <td className="px-2 py-2 text-right font-mono">{p.total}</td>
                  <td className="px-2 py-2 text-right font-mono font-bold">{p.this_week}</td>
                  <td className="px-2 py-2 text-right font-mono text-zinc-500">{p.last_week}</td>
                  <td className="px-4 py-2 text-right"><Trend value={p.change_pct} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
        <section className="overflow-x-auto rounded-2xl border-2 border-black bg-white">
          <h3 className="border-b-2 border-black bg-black px-4 py-3 font-display text-base font-bold text-white">Top queries · this week first</h3>
          <table className="w-full text-left text-sm" data-testid="analytics-queries">
            <thead className="text-[10px] uppercase tracking-wider text-zinc-500">
              <tr><th className="px-4 py-2">Query</th><th className="px-2 py-2 text-right">Total</th><th className="px-2 py-2 text-right">This wk</th><th className="px-4 py-2 text-right">Change</th></tr>
            </thead>
            <tbody className="divide-y divide-zinc-200">
              {data.queries.map((q) => (
                <tr key={`${q.product}|${q.query}`} data-testid="analytics-query-row">
                  <td className="px-4 py-2">
                    <span className="font-semibold">{q.query}</span>
                    <span className="block text-[11px] uppercase tracking-wider text-zinc-500">{q.product}</span>
                  </td>
                  <td className="px-2 py-2 text-right font-mono">{q.total}</td>
                  <td className="px-2 py-2 text-right font-mono font-bold">{q.this_week}</td>
                  <td className="px-4 py-2 text-right"><Trend value={q.change_pct} /></td>
                </tr>
              ))}
              {!data.queries.length && <tr><td colSpan={4} className="px-4 py-6 text-center text-zinc-500">No tickets in this range.</td></tr>}
            </tbody>
          </table>
        </section>
      </div>
    </div>
  );
}
